import { StatusFailureBanner } from "@/components/StatusFailureBanner";
import { useStatusFailureWebSocket } from "@/hooks/use-status-failure-ws";
import { authClient } from "@/lib/auth-client";
import { Facehash } from "facehash";
import { LogOutIcon, SettingsIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "./components/ui/dropdown-menu";
import { Spinner } from "./components/ui/spinner";
import { UnderConstruction } from "./components/UnderConstruction";
import "./index.css";
import { op } from "./lib/openpanel";

export function Layout() {
	const { data: session, isPending } = authClient.useSession();
	const location = useLocation();
	const isPublic = location.pathname.startsWith("/public");
	const isAuth =
		location.pathname.startsWith("/sign-in") ||
		location.pathname.startsWith("/sign-up") ||
		location.pathname.startsWith("/forgot-password") ||
		location.pathname.startsWith("/reset-password");
	const { lastFailure, setLastFailure } = useStatusFailureWebSocket();

	useEffect(() => {
		if (isPending) return;
		const track = async () => {
			if (session) {
				await op.identify({
					profileId: session.user.id,
					email: session.user.email,
					firstName: session.user.name,
				});
			}
			if (!session) {
				console.log("no session");
			}
			op.screenView(location.pathname);
		};
		track();
	}, [isPending, session, location.pathname]);

	return (
		<>
			<AnimatePresence>
				{isPending && (
					<motion.div
						key="loading-screen"
						initial={{ opacity: 1 }}
						animate={{ opacity: 0 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.3, delay: 0.5 }}
						className="absolute inset-0 bg-background flex flex-col justify-center items-center z-100 font-mono gap-2"
					>
						<h1 className="text-foreground text-2xl font-bold">statoos</h1>
						<Spinner size={24} />
					</motion.div>
				)}
			</AnimatePresence>
			{!isPublic && !isAuth && (
				<nav className="border-b border-border bg-background sticky top-0 z-20">
					<UnderConstruction className="w-full border-0 border-b h-12" />
					<StatusFailureBanner
						lastFailure={lastFailure}
						onDismiss={() => setLastFailure(null)}
					/>
					<div className="container flex h-14 items-center justify-between gap-6 max-w-3xl mx-auto px-4">
						<Link to="/" className="font-semibold transition-opacity font-mono">
							statoos
						</Link>
						{session && (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Facehash
										size={32}
										name={session.user.email}
										className="cursor-pointer border rounded-full"
									/>
								</DropdownMenuTrigger>
								<DropdownMenuContent
									align="end"
									sideOffset={12}
									className="min-w-40"
								>
									<DropdownMenuLabel className="flex flex-col gap-1">
										{session.user.name}
										<span className="text-muted-foreground text-sm font-normal">
											{session.user.email}
										</span>
									</DropdownMenuLabel>
									<DropdownMenuSeparator />
									<DropdownMenuItem asChild>
										<Link to="/settings">
											<SettingsIcon />
											Settings
										</Link>
									</DropdownMenuItem>
									<DropdownMenuSeparator />
									<DropdownMenuItem
										variant="destructive"
										onClick={() => authClient.signOut()}
									>
										<LogOutIcon />
										Sign out
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						)}
					</div>
				</nav>
			)}
			<main className="grow">
				<Outlet />
			</main>
		</>
	);
}

export default Layout;
